import * as React from 'react';
import { LayerType } from '../../../../shared/TileEditorTypes';

interface Layer {
  id: string;
  name: LayerType;
  visible: boolean;
  locked: boolean;
  opacity: number;
}

interface LayerPanelProps {
  layers: Layer[];
  activeLayer: LayerType;
  onLayerSelect: (layer: LayerType) => void;
  onLayerVisibilityToggle: (layerId: string) => void;
  onLayerLockToggle: (layerId: string) => void;
  onOpacityChange: (layerId: string, opacity: number) => void;
  onAddLayer: () => void;
  onRemoveLayer: (layerId: string) => void;
  onMoveLayerUp: (layerId: string) => void;
  onMoveLayerDown: (layerId: string) => void;
}

export const LayerPanel: React.FC<LayerPanelProps> = ({
  layers,
  activeLayer,
  onLayerSelect,
  onLayerVisibilityToggle,
  onLayerLockToggle,
  onOpacityChange,
  onAddLayer,
  onRemoveLayer,
  onMoveLayerUp,
  onMoveLayerDown
}) => {
  const getLayerIcon = (layerType: LayerType) => {
    switch (layerType) {
    case LayerType.GROUND: return '🌍';
    case LayerType.COLLISION: return '🛡️';
    case LayerType.OBJECTS: return '📦';
    case LayerType.TRIGGERS: return '⚡';
    case LayerType.ENTITIES: return '👤';
    default: return '📄';
    }
  };

  const getLayerColor = (layerType: LayerType) => {
    switch (layerType) {
    case LayerType.GROUND: return '#27ae60';
    case LayerType.COLLISION: return '#e74c3c';
    case LayerType.OBJECTS: return '#f39c12';
    case LayerType.TRIGGERS: return '#9b59b6';
    case LayerType.ENTITIES: return '#3498db';
    default: return '#95a5a6';
    }
  };

  return (
    <div style={{
      width: '280px',
      backgroundColor: '#34495e',
      color: 'white',
      padding: '15px',
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      height: '100%',
      overflowY: 'auto'
    }}>
      {/* Add Layer Button */}
      <button
        onClick={onAddLayer}
        style={{
          padding: '10px',
          backgroundColor: '#27ae60',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 'bold'
        }}
      >
        ➕ Add Layer
      </button>

      {/* Layers List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {layers.map((layer, index) => (
          <div
            key={layer.id}
            style={{
              backgroundColor: activeLayer === layer.name ? '#2c3e50' : '#2c3e50',
              border: `2px solid ${activeLayer === layer.name ? getLayerColor(layer.name) : 'transparent'}`,
              borderRadius: '6px',
              padding: '10px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative'
            }}
            onClick={() => onLayerSelect(layer.name)}
          >
            {/* Layer Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: getLayerColor(layer.name),
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}>
                {getLayerIcon(layer.name)}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                  {layer.name.charAt(0).toUpperCase() + layer.name.slice(1)}
                </div>
                <div style={{ fontSize: '11px', opacity: 0.7 }}>
                  {index === 0 ? 'Bottom' : index === layers.length - 1 ? 'Top' : `Layer ${index + 1}`}
                </div>
              </div>

              {/* Layer Controls */}
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onLayerVisibilityToggle(layer.id);
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: layer.visible ? '#27ae60' : '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px'
                  }}
                  title={layer.visible ? 'Hide Layer' : 'Show Layer'}
                >
                  {layer.visible ? '👁️' : '👁️‍🗨️'}
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onLayerLockToggle(layer.id);
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: layer.locked ? '#f39c12' : '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px'
                  }}
                  title={layer.locked ? 'Unlock Layer' : 'Lock Layer'}
                >
                  {layer.locked ? '🔒' : '🔓'}
                </button>
              </div>
            </div>

            {/* Layer Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Opacity Slider */}
              <div>
                <div style={{ fontSize: '11px', marginBottom: '4px', opacity: 0.8 }}>
                  Opacity: {Math.round(layer.opacity * 100)}%
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={layer.opacity * 100}
                  onChange={(e) => onOpacityChange(layer.id, parseInt(e.target.value) / 100)}
                  style={{ width: '100%' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Layer Actions */}
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveLayerUp(layer.id);
                  }}
                  disabled={index === 0}
                  style={{
                    flex: 1,
                    padding: '4px',
                    backgroundColor: index === 0 ? '#7f8c8d' : '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: index === 0 ? 0.5 : 1
                  }}
                  title="Move Up"
                >
                  ⬆️ Up
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveLayerDown(layer.id);
                  }}
                  disabled={index === layers.length - 1}
                  style={{
                    flex: 1,
                    padding: '4px',
                    backgroundColor: index === layers.length - 1 ? '#7f8c8d' : '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: index === layers.length - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: index === layers.length - 1 ? 0.5 : 1
                  }}
                  title="Move Down"
                >
                  ⬇️ Down
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveLayer(layer.id);
                  }}
                  disabled={layers.length <= 1}
                  style={{
                    flex: 1,
                    padding: '4px',
                    backgroundColor: layers.length <= 1 ? '#7f8c8d' : '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: layers.length <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: layers.length <= 1 ? 0.5 : 1
                  }}
                  title="Remove Layer"
                >
                  🗑️ Remove
                </button>
              </div>
            </div>

            {/* Active Layer Indicator */}
            {activeLayer === layer.name && (
              <div style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                width: '12px',
                height: '12px',
                backgroundColor: getLayerColor(layer.name),
                borderRadius: '50%'
              }} />
            )}
          </div>
        ))}
      </div>

      {/* Layer Help */}
      <div style={{
        backgroundColor: '#2c3e50',
        padding: '10px',
        borderRadius: '6px',
        fontSize: '12px',
        opacity: 0.8,
        marginTop: 'auto'
      }}>
        <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>Layer Guide:</div>
        <div>• <span style={{ color: '#27ae60' }}>Ground</span>: Base terrain tiles</div>
        <div>• <span style={{ color: '#e74c3c' }}>Collision</span>: Walls and obstacles</div>
        <div>• <span style={{ color: '#f39c12' }}>Objects</span>: Interactive items</div>
        <div>• <span style={{ color: '#9b59b6' }}>Triggers</span>: Events and zones</div>
        <div>• <span style={{ color: '#3498db' }}>Entities</span>: NPCs and spawns</div>
      </div>
    </div>
  );
};
